import {keepPreviousData} from "@tanstack/react-query";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {
    benchmarkDetailUrl,
    benchmarksUrl,
    type BenchmarksParams,
    exploreUrl,
    profileContractsUrl,
    profileHeaderUrl,
    profileSpendUrl,
    profileSuppliersUrl,
    type SuppliersParams,
    profileTransactionsUrl,
    riskScoreUrl,
    type TransactionsParams,
} from "../services/categoryProfileUrls";
import type {BenchmarkDetail, Benchmarks} from "../types/benchmarkTypes";
import type {
    ContractRow,
    Explore,
    ProfileHeader,
    RiskScore,
    SpendAnalysis,
    Suppliers,
    Transactions,
} from "../types/categoryProfileTypes";

function useGet<T>(url: string, enabled: boolean) {
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<T>(url)).data,
        enabled,
        // 403 (no access to the dataset) and 404 (no spend for the category) are answers, not glitches.
        retry: false,
    });
}

/** Explore Categories: the whole tree plus header figures (`GET category_profile/{bag}/explore/`). */
export function useExploreQuery(databagId: number, months?: number) {
    return useGet<Explore>(exploreUrl(databagId, months), databagId > 0);
}

/** One category's risk score with its explanation; ask when it is shown (a tooltip), not for the whole tree. */
export function useRiskScoreQuery(databagId: number, path: string) {
    return useGet<{risk: RiskScore | null}>(riskScoreUrl(databagId, path), databagId > 0 && Boolean(path));
}

export function useProfileHeaderQuery(databagId: number, path: string) {
    return useGet<ProfileHeader>(profileHeaderUrl(databagId, path), databagId > 0 && Boolean(path));
}

export function useProfileSpendQuery(databagId: number, path: string) {
    return useGet<SpendAnalysis>(profileSpendUrl(databagId, path), databagId > 0 && Boolean(path));
}

/** Server-paged suppliers; keeps the previous page on screen while the next one loads. */
export function useProfileSuppliersQuery(databagId: number, params: SuppliersParams) {
    const url = profileSuppliersUrl(databagId, params);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<Suppliers>(url)).data,
        enabled: databagId > 0 && Boolean(params.path),
        placeholderData: keepPreviousData,
        retry: false,
    });
}

export function useProfileContractsQuery(databagId: number, path: string) {
    return useGet<ContractRow[]>(profileContractsUrl(databagId, path), databagId > 0 && Boolean(path));
}

/** Server-paged transactions; keeps the previous page on screen while the next one loads. */
export function useProfileTransactionsQuery(databagId: number, params: TransactionsParams) {
    const url = profileTransactionsUrl(databagId, params);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<Transactions>(url)).data,
        enabled: databagId > 0 && Boolean(params.path),
        placeholderData: keepPreviousData,
        retry: false,
    });
}

/** Internal benchmark of the category: summary, a server-sorted page of item groups, and price per SKU. */
export function useBenchmarksQuery(databagId: number, params: BenchmarksParams) {
    const url = benchmarksUrl(databagId, params);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<Benchmarks>(url)).data,
        enabled: databagId > 0 && Boolean(params.path),
        placeholderData: keepPreviousData,
        retry: false,
    });
}

/** One item group's sites, business units and suppliers (loaded when its row is expanded). */
export function useBenchmarkDetailQuery(databagId: number, path: string, id: number) {
    return useGet<BenchmarkDetail>(benchmarkDetailUrl(databagId, path, id), databagId > 0 && Boolean(path));
}
